import React, { useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useCart } from '../../context/CartContext';
import { useStore } from '../../context/StoreContext';
import { ShoppingBag, User as UserIcon, Search, Menu, X, Shield, ArrowRight } from 'lucide-react';

interface NavbarProps {
  currentPath: string;
  onNavigate: (path: string) => void;
}

export function Navbar({ currentPath, onNavigate }: NavbarProps) {
  const { user } = useAuth();
  const { itemCount, openCart } = useCart();
  const { settings, categories } = useStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      onNavigate(`/catalogo?search=${encodeURIComponent(searchQuery.trim())}`);
      setSearchOpen(false);
      setSearchQuery('');
    }
  };

  return (
    <>
      <header className="sticky top-0 z-40 bg-background/95 backdrop-blur-md border-b border-border-subtle transition-all">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-20">
            {/* Left: Mobile Trigger & Desktop Category Nav */}
            <div className="flex items-center gap-6">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-2 text-ink hover:text-primary transition-colors -ml-2"
                aria-label="Abrir menu de navegação"
              >
                {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>

              <nav className="hidden lg:flex items-center gap-6 text-xs uppercase tracking-widest font-medium">
                <button
                  onClick={() => onNavigate('/catalogo')}
                  className={`transition-colors hover:text-primary ${
                    currentPath === '/catalogo' ? 'text-primary font-semibold' : 'text-ink-muted'
                  }`}
                >
                  Catálogo
                </button>
                {categories.slice(0, 4).map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => onNavigate(`/categoria/${cat.slug}`)}
                    className={`transition-colors hover:text-primary ${
                      currentPath === `/categoria/${cat.slug}` ? 'text-primary font-semibold' : 'text-ink-muted'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </nav>
            </div>

            {/* Center: Brand Identity */}
            <div className="flex flex-col items-center cursor-pointer select-none" onClick={() => onNavigate('/')}>
              <span className="font-serif text-2xl sm:text-3xl tracking-tight font-medium text-ink">
                {settings.brand_name || 'Aura Ateliê'}
              </span>
              <span className="text-[9px] uppercase tracking-[0.25em] text-ink-muted -mt-0.5">
                Design & Ofício
              </span>
            </div>

            {/* Right: Search, Account, Bag */}
            <div className="flex items-center gap-3 sm:gap-4">
              {/* Search Toggle */}
              <button
                onClick={() => setSearchOpen(true)}
                className="p-2 text-ink hover:text-primary transition-colors"
                aria-label="Buscar no catálogo"
              >
                <Search className="w-5 h-5" />
              </button>

              {/* Admin Quick Link */}
              {user?.role === 'admin' && (
                <button
                  onClick={() => onNavigate('/admin')}
                  className="hidden sm:flex items-center gap-1.5 px-2.5 py-1 text-xs font-mono bg-ink text-ink-inverted rounded-xs hover:bg-primary transition-colors"
                >
                  <Shield className="w-3.5 h-3.5 text-primary-dark" />
                  Admin
                </button>
              )}

              {/* Account Link */}
              <button
                onClick={() => onNavigate(user ? '/conta' : '/login')}
                className="p-2 text-ink hover:text-primary transition-colors relative"
                aria-label={user ? 'Minha conta' : 'Entrar ou registrar'}
              >
                <UserIcon className="w-5 h-5" />
                {user && (
                  <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-olive rounded-full" />
                )}
              </button>

              {/* Shopping Bag Trigger */}
              <button
                onClick={openCart}
                className="p-2 text-ink hover:text-primary transition-colors relative flex items-center"
                aria-label="Abrir sacola de compras"
              >
                <ShoppingBag className="w-5 h-5" />
                {itemCount > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-primary text-white text-[10px] font-mono font-bold w-4 h-4 rounded-full flex items-center justify-center leading-none">
                    {itemCount}
                  </span>
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Navigation Drawer */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-border-subtle bg-surface px-6 py-6 space-y-4 animate-fadeIn">
            <p className="text-[10px] uppercase tracking-widest text-ink-muted font-medium">Navegação</p>
            <div className="flex flex-col space-y-3 text-sm">
              <button
                onClick={() => {
                  onNavigate('/catalogo');
                  setMobileMenuOpen(false);
                }}
                className="text-left font-serif text-lg text-ink hover:text-primary"
              >
                Ver Catálogo Completo
              </button>
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  onClick={() => {
                    onNavigate(`/categoria/${cat.slug}`);
                    setMobileMenuOpen(false);
                  }}
                  className="text-left text-ink hover:text-primary"
                >
                  {cat.name}
                </button>
              ))}
              <hr className="border-border-subtle my-2" />
              <button
                onClick={() => {
                  onNavigate('/politicas/sobre');
                  setMobileMenuOpen(false);
                }}
                className="text-left text-xs uppercase tracking-wider text-ink-muted hover:text-ink"
              >
                Sobre o Ateliê
              </button>
              <button
                onClick={() => {
                  onNavigate('/politicas/contato');
                  setMobileMenuOpen(false);
                }}
                className="text-left text-xs uppercase tracking-wider text-ink-muted hover:text-ink"
              >
                Atendimento & Concierge
              </button>
              {user?.role === 'admin' && (
                <button
                  onClick={() => {
                    onNavigate('/admin');
                    setMobileMenuOpen(false);
                  }}
                  className="text-left text-xs uppercase tracking-wider font-semibold text-primary"
                >
                  Painel Administrativo &rarr;
                </button>
              )}
            </div>
          </div>
        )}
      </header>

      {/* Quick Search Modal */}
      {searchOpen && (
        <div
          className="fixed inset-0 z-50 bg-ink/50 backdrop-blur-xs flex items-start justify-center pt-24 px-4"
          role="dialog"
          aria-modal="true"
        >
          <div className="bg-surface w-full max-w-xl rounded-md shadow-elevated border border-border-subtle p-6 animate-fadeIn">
            <div className="flex items-center justify-between pb-4 border-b border-border-subtle">
              <span className="text-xs uppercase tracking-widest font-medium text-ink-muted">Busca no Catálogo</span>
              <button
                onClick={() => setSearchOpen(false)}
                className="text-ink-muted hover:text-ink p-1"
                aria-label="Fechar busca"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <form onSubmit={handleSearchSubmit} className="mt-4 flex gap-2">
              <input
                type="text"
                autoFocus
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Busque por cerâmica, linho, presets, consultorias..."
                className="flex-1 bg-background border border-border-subtle px-4 py-2.5 rounded-sm text-sm focus:outline-none focus:border-primary"
              />
              <button
                type="submit"
                className="bg-ink text-ink-inverted px-4 py-2.5 rounded-sm text-xs uppercase tracking-wider font-medium hover:bg-primary transition-colors flex items-center gap-1.5"
              >
                Buscar
                <ArrowRight className="w-3.5 h-3.5" />
              </button>
            </form>
          </div>
        </div>
      )}
    </>
  );
}
